import { useLocation, useNavigate } from 'react-router-dom';
import { isAllTabInUse } from '../selectors/tabs-selectors';
import type { AppTemplate } from '@wix/ambassador-devcenter-apps-v1-app-template/types';
import { QueryParams } from '../constants/QueryParams';
import { scrollBodyToTop } from '../utils/scrollBodyToTop';
import {
  filterToUrlParamMapper,
  getUrlSearchParamsString,
  type UrlParams,
  urlParamToFilterMapper,
} from '../utils/urlParams-utils';
import type { TemplateTab } from '../types/TemplateTabs';
import { useQueryParams } from './useQueryParams';
import { isQueryParamFlagsTrue } from '../utils/isQueryParamFlagsTrue';
import type { BlocksTemplateUrlProps } from '../types/BlocksTemplateUrlProps';
import { getBlocksTemplateUrl } from '../utils/getBlocksTemplateUrl';

export const usePageNavigate = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { setParam, removeParam, getParam } = useQueryParams();
  const templateId = getParam(QueryParams.ID);
  const filterParamInUrl = getParam(QueryParams.FILTER);
  const httpReferrer = getParam(QueryParams.HTTP_REFERRER);
  const shouldHideSidebar = isQueryParamFlagsTrue(
    getParam(QueryParams.HIDE_SIDEBAR),
  );
  const appliedFilter = urlParamToFilterMapper[filterParamInUrl];
  const currentOriginTab = location?.state?.originTab;

  const openBlocksTemplateFlow = (props: BlocksTemplateUrlProps) => {
    const blocksUrl = getBlocksTemplateUrl({
      ...props,
      httpReferrer,
      entryFlow: 'templates-page',
    });

    window.open(blocksUrl);
  };

  const getExternalTemplateUrl = (template: AppTemplate) => {
    return template.previewUrl // if there is a preview url, use internal template page
      ? ''
      : getBlocksTemplateUrl({
          templateId: template.id,
          templateName: template.title,
          httpReferrer,
          entryFlow: 'templates-page',
          otherParams: { [QueryParams.FUNNEL_AUTO_REDIRECT]: 'true' },
        });
  };

  const navigateToMainPage = () => {
    const search = getUrlSearchParamsString({
      [QueryParams.FILTER]: currentOriginTab,
      [QueryParams.HTTP_REFERRER]: httpReferrer,
    });
    navigate({
      pathname: '/',
      ...(search && { search }),
    });

    scrollBodyToTop();
  };

  const navigateToTemplate = (
    template: AppTemplate,
    originTab: UrlParams | undefined = currentOriginTab,
  ) => {
    const externalTemplateUrl = getExternalTemplateUrl(template);
    if (externalTemplateUrl) {
      window.open(externalTemplateUrl);
      return;
    } else {
      navigate(
        {
          pathname: '/template',
          search: getUrlSearchParamsString({
            [QueryParams.ID]: template.id,
            [QueryParams.HTTP_REFERRER]: httpReferrer,
          }),
        },
        { state: { originTab } },
      );
      scrollBodyToTop();
    }
  };

  const setParamAccordingToTab = (chosenTab: TemplateTab) => {
    const chosenTabToUrlParam = filterToUrlParamMapper?.[chosenTab];
    if (isAllTabInUse(chosenTab)) {
      removeParam(QueryParams.FILTER);
    } else {
      setParam(QueryParams.FILTER, chosenTabToUrlParam);
    }
  };

  return {
    templateId,
    appliedFilter,
    shouldHideSidebar,
    navigateToTemplate,
    navigateToMainPage,
    setParamAccordingToTab,
    openBlocksTemplateFlow,
    getExternalTemplateUrl,
  };
};
